/**
 * FakeoutWarningsPanel - High-Risk Level Warnings Display
 *
 * Displays levels with >30% fakeout rate with depth indicators.
 * Collapsible panel with red warning styling.
 *
 * Height: ~60px collapsed, expandable
 */

import React, { useState, memo } from 'react';
import { Box, Typography, Collapse, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * Format trend direction for display
 */
const formatTrend = (trend) => {
    if (!trend) return '--';
    return trend
        .replace('STRONG_', 'S.')
        .replace('MODERATE_', 'M.')
        .replace('UPTREND', 'UP')
        .replace('DOWNTREND', 'DN');
};

/**
 * Format ADX range for display
 */
const formatAdxRange = (range) => {
    if (!range) return '--';
    return range
        .replace('STRONG_TREND', 'STRONG')
        .replace('GOOD_TREND', 'GOOD')
        .replace('WEAK_TREND', 'WEAK')
        .replace('RANGING', 'RANGE');
};

/**
 * Single warning row
 */
const WarningRow = memo(({ warning, onClick }) => {
    const levelLabel = `${warning.levelType} ${warning.levelValue || ''}`.trim();
    const condition = formatTrend(warning.trendDirection) || formatAdxRange(warning.adxRange) || '--';
    const fakeoutRate = warning.fakeoutRate;
    const p75Depth = warning.p75FakeoutDepth;
    const p95Depth = warning.p95FakeoutDepth;

    // Format depth range
    const depthRange = p75Depth != null && p95Depth != null
        ? `${p75Depth.toFixed(1)}-${p95Depth.toFixed(1)}`
        : p75Depth != null
            ? `~${p75Depth.toFixed(1)}`
            : '--';

    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '4px 8px',
                backgroundColor: 'rgba(239, 68, 68, 0.05)',
                borderBottom: '1px solid rgba(239, 68, 68, 0.1)',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': onClick ? { backgroundColor: 'rgba(239, 68, 68, 0.1)' } : {},
                '&:last-child': { borderBottom: 'none' }
            }}
        >
            {/* Level + Condition */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '10px', color: '#ef4444', fontFamily: 'monospace', fontWeight: 700 }}>
                    {levelLabel}
                </Typography>
                <Typography sx={{ fontSize: '9px', color: '#f87171', fontFamily: 'monospace' }}>
                    + {condition}
                </Typography>
            </Box>

            {/* Fakeout Rate */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Tooltip title={`${warning.totalEvents || 0} events analyzed`}>
                    <Typography sx={{ fontSize: '10px', color: '#ef4444', fontFamily: 'monospace', fontWeight: 700 }}>
                        {fakeoutRate?.toFixed(0)}% fake
                    </Typography>
                </Tooltip>

                {/* Depth Range */}
                <Tooltip title="Typical fakeout depth (p75-p95)">
                    <Typography sx={{ fontSize: '9px', color: '#fbbf24', fontFamily: 'monospace' }}>
                        Depth: {depthRange} pips
                    </Typography>
                </Tooltip>
            </Box>
        </Box>
    );
});

WarningRow.displayName = 'WarningRow';

/**
 * FakeoutWarningsPanel Component
 */
const FakeoutWarningsPanel = memo(({
    warnings = [],
    defaultExpanded = false,
    onWarningClick,
    maxVisible = 3
}) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    // No warnings - don't render
    if (!warnings || warnings.length === 0) {
        return null;
    }

    const hasMore = warnings.length > maxVisible;
    const visibleWarnings = expanded ? warnings : warnings.slice(0, maxVisible);
    const hiddenCount = warnings.length - maxVisible;

    return (
        <Box
            sx={{
                backgroundColor: '#0a0a0a',
                borderRadius: '2px',
                overflow: 'hidden',
                border: '1px solid rgba(239, 68, 68, 0.3)'
            }}
        >
            {/* Header */}
            <Box
                onClick={() => hasMore && setExpanded(!expanded)}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '5px 8px',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    cursor: hasMore ? 'pointer' : 'default',
                    '&:hover': hasMore ? { backgroundColor: 'rgba(239, 68, 68, 0.15)' } : {}
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                    <Typography sx={{ fontSize: '10px', color: '#ef4444', fontWeight: 700, fontFamily: 'monospace' }}>
                        FAKEOUT WARNINGS
                    </Typography>
                    <Typography sx={{ fontSize: '9px', color: '#f87171', fontFamily: 'monospace' }}>
                        ({warnings.length} level{warnings.length !== 1 ? 's' : ''} at risk)
                    </Typography>
                </Box>

                {hasMore && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: 'monospace' }}>
                            {expanded ? 'Show less' : `+${hiddenCount} more`}
                        </Typography>
                        <ExpandMoreIcon
                            sx={{
                                fontSize: 14,
                                color: '#6b7280',
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s'
                            }}
                        />
                    </Box>
                )}
            </Box>

            {/* Warning Rows */}
            <Box>
                {visibleWarnings.map((warning, index) => (
                    <WarningRow
                        key={`${warning.levelType}-${warning.levelValue}-${index}`}
                        warning={warning}
                        onClick={onWarningClick ? () => onWarningClick(warning) : undefined}
                    />
                ))}

                {/* Collapsed indicator */}
                {!expanded && hasMore && (
                    <Collapse in={!expanded}>
                        <Box
                            sx={{
                                padding: '4px 8px',
                                textAlign: 'center',
                                backgroundColor: 'rgba(239, 68, 68, 0.03)'
                            }}
                        >
                            <Typography sx={{ fontSize: '9px', color: '#6b7280', fontFamily: 'monospace' }}>
                                Click header to expand all {warnings.length} warnings
                            </Typography>
                        </Box>
                    </Collapse>
                )}
            </Box>
        </Box>
    );
});

FakeoutWarningsPanel.displayName = 'FakeoutWarningsPanel';

export default FakeoutWarningsPanel;
